import { clamp, lerpPoint, round, type Point } from './geometry';

/**
 * The connector is generated at its final on-screen size and drawn as a *stroked*
 * curve, so the dash width and the arrowhead are identical whatever the distance
 * or direction. (The previous version scaled one fixed Figma path non-uniformly,
 * which squashed the dashes and the head into a stretched smear.)
 */

export interface ArrowHead {
  x: number;
  y: number;
  /** Degrees, so it can go straight into an SVG `rotate()`. */
  angle: number;
  length: number;
  width: number;
}

export interface ArrowGeometry {
  /** Path data for the dashed curve, already trimmed to make room for the head. */
  d: string;
  head: ArrowHead;
}

const MAX_HEAD_LENGTH = 15;
/** Small enough to still read as an arrowhead on a phone-sized hop. */
const MIN_HEAD_LENGTH = 9;
const HEAD_ASPECT = 13 / 15;

/**
 * A short arrow shrinks its head instead of disappearing. On a phone the card
 * often sits only ~50px from the spotlight, and a fixed 15px head would eat the
 * whole run — which is why those steps used to render no arrow at all.
 */
function headLengthFor(distance: number): number {
  return clamp(distance * 0.3, MIN_HEAD_LENGTH, MAX_HEAD_LENGTH);
}

/** Below this even a shrunken head has no curve left to sit on. */
export const MIN_ARROW_LENGTH = 26;

const LENGTH_SAMPLES = 24;

function quadraticPoint(from: Point, control: Point, to: Point, t: number): Point {
  const mt = 1 - t;
  return {
    x: mt * mt * from.x + 2 * mt * t * control.x + t * t * to.x,
    y: mt * mt * from.y + 2 * mt * t * control.y + t * t * to.y,
  };
}

function quadraticLength(from: Point, control: Point, to: Point): number {
  let length = 0;
  let previous = from;
  for (let i = 1; i <= LENGTH_SAMPLES; i++) {
    const point = quadraticPoint(from, control, to, i / LENGTH_SAMPLES);
    length += Math.hypot(point.x - previous.x, point.y - previous.y);
    previous = point;
  }
  return length;
}

/** Control point of an arc bowed `bow` px sideways from the straight chord. Sign picks the side. */
export function arcControlPoint(from: Point, to: Point, bow: number): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  return {
    x: (from.x + to.x) / 2 - (dy / length) * bow,
    y: (from.y + to.y) / 2 + (dx / length) * bow,
  };
}

export function arrowLength(from: Point, to: Point): number {
  return Math.hypot(to.x - from.x, to.y - from.y);
}

/**
 * Build the dashed arc that runs from the card edge to the spotlight.
 * Returns null when the two points are too close for a readable arrow.
 */
export function buildArrowGeometry(from: Point, to: Point, bow: number): ArrowGeometry | null {
  const length = arrowLength(from, to);
  if (!Number.isFinite(length) || length < MIN_ARROW_LENGTH) return null;

  const control = arcControlPoint(from, to, bow);
  const total = quadraticLength(from, control, to);
  const headLength = headLengthFor(total);

  // Stop the stroke one head short of the tip and split the curve there, so the
  // dashes never poke out through the arrowhead.
  const t = clamp(1 - headLength / Math.max(total, 1), 0.02, 0.98);
  const end = quadraticPoint(from, control, to, t);
  const trimmedControl = lerpPoint(from, control, t);

  return {
    d: `M ${round(from.x)} ${round(from.y)} Q ${round(trimmedControl.x)} ${round(trimmedControl.y)} ${round(end.x)} ${round(end.y)}`,
    head: {
      x: round(to.x),
      y: round(to.y),
      angle: round((Math.atan2(to.y - end.y, to.x - end.x) * 180) / Math.PI, 1),
      length: round(headLength, 1),
      width: round(headLength * HEAD_ASPECT, 1),
    },
  };
}
